/**
 * Where the camera stands for a picture of the room that the user has not
 * framed themselves: a corner view, as the Blender renderer's classic corner
 * framing (scripts/blender/render_room.py `cameras` with `cornerVaryFt` 0 and
 * `cornerClear` off), so the fast preview the browser takes and the photoreal
 * "this view" picture are the same picture. The renderer's own four corner
 * views may stand higher or lower and step in from a blocked corner (the
 * render settings `camera.cornerVaryFt` and `camera.cornerClear`).
 *
 * Viewer world coordinates: the room centred on the origin, x along its
 * length, z along its width, y up; the front wall at z = −W/2, the back at
 * +W/2, the left at x = −L/2, the right at +L/2 (Room3DViewer: the room group
 * sits at (−L/2, 0, −W/2)).
 *
 * The corner is chosen on the side OPPOSITE the room's feature wall (the
 * garage door's, the vanity's, …: panorama.ts `panoramaFace`), so that wall
 * is in the picture; the free axis puts the most-finished of the two adjacent
 * walls in view too.
 */

import type { RoomSurfaces } from '../../../types/sections';
import type { WallFace } from './types';

export interface CameraPose {
  position: [number, number, number];
  target: [number, number, number];
  /** Vertical field of view, degrees. */
  fov: number;
}

export interface Corner { sx: 1 | -1; sz: 1 | -1 }

/** Blender's corner framing (render_room.py). */
export const CORNER_INSET_FT = 1.5;
export const CORNER_AIM_INSET_FT = 3.0;
export const CORNER_FOV = 68;

/** Which way a wall is from the centre, as the sign of the axis it lies on. */
const SIDE: Record<WallFace, Partial<Corner>> = {
  front: { sz: -1 },
  back: { sz: 1 },
  left: { sx: -1 },
  right: { sx: 1 },
};

/**
 * The corner a camera stands in to see `feature` (and `secondary`, when it is
 * on the other axis): the camera is on the opposite side of each. With no
 * secondary, the free axis is +1.
 */
export function cornerFor(feature: WallFace, secondary?: WallFace): Corner {
  const f = SIDE[feature];
  const s = secondary ? SIDE[secondary] : {};
  const sx = f.sx !== undefined ? -f.sx : s.sx !== undefined ? -s.sx : 1;
  const sz = f.sz !== undefined ? -f.sz : s.sz !== undefined ? -s.sz : 1;
  return { sx: sx as 1 | -1, sz: sz as 1 | -1 };
}

/** The camera in `corner`, at eye height, aimed across the room at the opposite corner: Blender's numbers. */
export function cornerPose(dims: { length: number; width: number; height: number }, corner: Corner): CameraPose {
  const { length: L, width: W, height: H } = dims;
  const eye = Math.min(5.5, H - 1.0);
  const aim = Math.min(4.5, H - 2.0);
  return {
    position: [(L / 2 - CORNER_INSET_FT) * corner.sx, eye, (W / 2 - CORNER_INSET_FT) * corner.sz],
    target: [-(L / 2 - CORNER_AIM_INSET_FT) * corner.sx, aim, -(W / 2 - CORNER_AIM_INSET_FT) * corner.sz],
    fov: CORNER_FOV,
  };
}

/** The wall with the most product on it, among those not excluded; undefined when none has any. */
export function mostFinishedSideWall(surfaces: RoomSurfaces, exclude: WallFace[]): WallFace | undefined {
  const faces: WallFace[] = ['front', 'right', 'back', 'left'];
  let best: WallFace | undefined;
  let most = 0;
  surfaces.walls.forEach((w, i) => {
    const face = faces[i];
    if (!face || exclude.includes(face)) return;
    const area = w.sections.reduce((sum, s) => sum + s.width * s.height, 0);
    if (area > most) { most = area; best = face; }
  });
  return best;
}

/** The wall across from `face`. */
export function oppositeWall(face: WallFace): WallFace {
  return face === 'front' ? 'back' : face === 'back' ? 'front' : face === 'left' ? 'right' : 'left';
}
